<template>
    <main class="tx-code-json">
        <a class="copy-raw-body-button" v-if="rawBody" v-on:click="copyRawBody">Copy raw body</a>
        <div class="tx-code-nav">
            <div
                v-if="!noTabs"
                class="tx-code-nav__item"
                v-bind:class="{'tx-code-nav__item--active': currentTab === 'decoded'}"
                v-on:click="changeTab('decoded')"
            >decoded</div>
            <div
                v-if="!noTabs"
                class="tx-code-nav__item"
                v-bind:class="{'tx-code-nav__item--active': currentTab === 'json'}"
                v-on:click="changeTab('json')"
            >json</div>
        </div>

        <div class="tx-page-msg" v-bind:class="{'tx-code': !noTabs}" style="max-width: 870px;overflow: auto">
            <pre v-show="currentTab === 'decoded'" class="tx-body-decoded-code" v-html="formattedDescription" />

            <pre v-show="currentTab === 'json'" class="tx-body-decoded-code">{{ transactionDescription }}</pre>
        </div>
    </main>
</template>

<script>
import { showToast } from '~/toast';
import yaml from 'js-yaml';
import hljs from 'highlight.js/lib/core';
import yamlHighlight from 'highlight.js/lib/languages/yaml';
import { Address } from '~/tonweb';

function formatNestedObject(obj) {
    const type = obj['@type'];

    if (type) {
        // No address
        if (type === 'addr_none') {
            return 'addr_none';
        }

        // Standard address
        if (type === 'addr_std') {
            const wc = obj.workchain_id;
            const address = obj.address;
            return new Address(`${wc}:${address}`).toString({
                userFriendly: true,
                bouncable: true,
            });
        }

        // Amount
        if (type === 'nanograms' || type === 'nanocoins') {
            return parseInt(obj.amount.value, 10);
        }

        // Comments/text
        if (type === 'text_comment') {
            return { text: obj?.text, comment: obj?.comment };
        }
    }

    const result = obj;

    Object.keys(obj).forEach((key) => {
        const value = obj[key];

        if (typeof value === 'string') {
            const trimmed = value.trim();

            // Format numbers if they are in string format
            if (/^-?\d+$/.test(trimmed)) {
                result[key] = parseInt(trimmed, 10);
                return;
            }

            result[key] = trimmed;
            return;
        }

        result[key] = value;
    });

    return result;
}

function deepMap(obj, path = []) {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }

    // Cycle protection
    if (!deepMap._seen) deepMap._seen = new WeakSet(); // eslint-disable-line no-underscore-dangle
    if (deepMap._seen.has(obj)) return obj; // eslint-disable-line no-underscore-dangle
    deepMap._seen.add(obj); // eslint-disable-line no-underscore-dangle

    // Formatting
    const formatted = formatNestedObject(obj);

    if (formatted === null || typeof formatted !== 'object') return formatted;

    // Making copy
    const result = Array.isArray(formatted) ? [] : {};

    if (Array.isArray(formatted)) {
        for (let i = 0; i < formatted.length; i += 1) {
            // Recursive
            result[i] = deepMap(formatted[i], path.concat(i));
        }
    } else {
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, value] of Object.entries(formatted)) {
            // Changing "@type" -> "type"
            const newKey = key === '@type' ? 'type' : key;

            // Recursive
            result[newKey] = deepMap(value, path.concat(newKey));
        }
    }

    return result;
}

export default {
    props: {
        transactionDescription: Object,
        rawBody: String,
        hiddenJson: {
            type: Boolean,
            default: false,
        },
        noTabs: {
            type: Boolean,
            default: false,
        },
        op: String,
    },

    data() {
        return {
            hiddenActive: false,
            description: {},
            currentTab: 'decoded',
        };
    },

    mounted() {
        if (this.noTabs) this.changeTab('json');
    },

    computed: {
        copyContent() {
            let contentToCopy;

            if (this.currentTab === 'decoded') {
                const d = this.transactionDescription?.data || this.transactionDescription;
                const newDescription = JSON.parse(JSON.stringify(d));

                contentToCopy = yaml.dump(newDescription, {
                    indent: 2,
                    lineWidth: -1,
                    noRefs: true,
                    sortKeys: false,
                    flowLevel: -1,
                });
            } else if (this.currentTab === 'json') {
                contentToCopy = JSON.stringify(this.transactionDescription);
            }

            return contentToCopy;
        },

        formattedDescription() {
            const jsonDescription = JSON.parse(JSON.stringify(this.transactionDescription));

            // Formatting body
            const newDescription = deepMap(jsonDescription);

            const formattedToYaml = yaml.dump(newDescription, {
                indent: 2,
                lineWidth: -1,
                noRefs: true,
                sortKeys: false,
                flowLevel: -1,
            });

            hljs.registerLanguage('yaml', yamlHighlight);

            return hljs.highlight(
                formattedToYaml,
                { language: 'yaml' },
            ).value;
        },
    },

    methods: {
        // toggleContent() {
        //     this.hiddenActive = !this.hiddenActive;
        // },

        changeTab(tab) {
            this.currentTab = tab;
        },

        async copyRawBody() {
            try {
                await navigator.clipboard.writeText(this.rawBody);
            } catch {
                this.copyToClipboardFallback(this.rawBody);
            }

            showToast('Copied!');
        },
    },
};
</script>

<style lang="scss">
.tx-code-json {
    position: relative;

    &__hide {
        height: 95px;
        overflow: hidden;
    }

    &__hide::after {
        content: "";
        position: absolute;
        bottom: 1px;
        left: 1px;
        right: 1px;
        height: 120px;
        background: linear-gradient(to top, var(--hidden-json-expander) 20%, transparent);
        pointer-events: none;
        border-radius: 0 0 8px 8px;
    }
}

.tx-code {
    padding: 10px;
    display: block;
    white-space: pre-wrap;
    word-break: break-all;
    z-index: 9;
    border-radius: 0 8px 8px 8px!important;
}

.tx-code-nav {
    display: flex;
    margin-bottom: -1px;
    z-index: 10;

    &__item {
        border: 1px solid var(--card-row-separator);
        font-size: 13px;
        overflow: hidden;
        padding: 8px 18px;
        cursor: pointer;
        color: var(--body-muted-text-color);

        &:only-child {
            border-top-left-radius: 8px;
            border-top-right-radius: 8px;
            border-left-width: 1px!important;
        }

        &:first-child {
            border-top-left-radius: 8px;
        }

        &:last-child {
            border-left-width: 0;
            border-top-right-radius: 8px;
        }

        &--active {
            color: var(--body-text-color);
            background: var(--tx-messages-message-background);
            border-bottom: var(--tx-messages-message-background);
        }
    }
}

.copy-raw-body-button {
    display: block;
    position: absolute;
    top: 6px;
    right: 4px;
    font-size: 13px;
}

.tx-code-json-copy {
    background: var(--tx-messages-message-background);
}

.tx-code-expander {
    position: absolute;
    bottom: 20px;
    left: 50%;
    font-weight: 500;
    cursor: pointer;
    z-index: 900;
    transform: translate(-50%, 0%);
}

.tx-body-decoded-code {
    margin: 0;
    font-size: 14px;

    .hljs-attr {
        color: var(--body-text-color);
        //opacity: .6;
    }

    .hljs-string,
    .hljs-number {
        color: var(--blue-bright);
    }
}
</style>
